import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react'
import classnames from 'classnames'

import config from 'config'
import { AUTH_STEPS } from 'constants/auth'
import { TIMESTAMP_KEYS } from 'constants/app'
import { USER_DATA_FIELDS, USER_DATA_KEYS } from 'constants/user-data'
import { APPLICATION_STATUS_ACTIONS } from 'constants/application-status'
import { AppContext } from 'contexts/AppContext'
import { apiManager } from 'helpers'
import { getProfileActivities } from 'helpers/application-status'
import { useQuery } from 'hooks'

import Button from 'components/Button'
import { AuthModal } from 'components'
import UserData from 'components/Discover/UserData'
import MembershipModal from 'components/MembershipModal'
import ModalBackground from 'components/Discover/ModalBackground'
import Ranking from 'components/Discover/Ranking'

import logo from 'assets/images/logo.gif'

const getMissingFieldsData = ({ key, user }) => {
  const values = user[key]
  const allFields = (USER_DATA_FIELDS[key] || []).filter(({ dependsOn }) => !dependsOn)
  const firstMissingField = allFields.find(field => {
    if (!field.required) {
      return false
    }

    if (field.type === 'range') {
      return !values[field.minField] || !values[field.maxField]
    }

    const { key } = field

    return !values[key] || values[key].length === 0
  })
  if (!firstMissingField) {
    return null
  }

  return {
    key,
    totalFields: allFields.length,
    completedFields: allFields.indexOf(firstMissingField),
  }
}

const ButtonLg = ({ children, className, textClassName, ...rest }) => (
  <Button className={classnames('w-full rounded-xl h-16 mb-3', className)} {...rest}>
    <span
      className={classnames(
        'text-black font-normal text-base uppercase font-medium font-ownersWide',
        textClassName
      )}
    >
      {children}
    </span>
  </Button>
)

const Waitlist = ({ user }) => {
  const { timestamps, updateTimestamp } = useContext(AppContext)
  const [showApplicationData, setShowApplicationData] = useState(false)

  const [showProfileModal, setShowProfileModal] = useState(false)
  const [showMembershipModal, setShowMembershipModal] = useState(false)

  const { data: waitlistData } = useQuery('user/waitlist', apiManager.getUserWaitlist)
  const { data: activityData, refetch: refetchApplicationStatus } = useQuery(
    'user/applicationStatus',
    apiManager.getApplicationStatus
  )

  const { isProfileInComplete, isPaymentInComplete } = useMemo(() => {
    const profileActivities = getProfileActivities(activityData?.activities)
    const isProfileInComplete = profileActivities.some(({ completed }) => !completed)
    const savePaymentAction = activityData?.activities.find(
      ({ action }) => action === APPLICATION_STATUS_ACTIONS.SAVE_PAYMENT
    )
    const isPaymentInComplete = savePaymentAction && !savePaymentAction.completed

    return {
      isProfileInComplete,
      isPaymentInComplete,
    }
  }, [activityData])

  const incompleteApplicationData = useMemo(() => {
    const missingProfileData = getMissingFieldsData({
      key: USER_DATA_KEYS.PROFILE,
      user,
    })
    if (missingProfileData) {
      return missingProfileData
    }

    return getMissingFieldsData({
      key: USER_DATA_KEYS.PREFERENCES,
      user,
    })
  }, [user])

  const isApplicationCompleted = useMemo(
    () => !isProfileInComplete && !incompleteApplicationData,
    [isProfileInComplete, incompleteApplicationData]
  )

  const title = useMemo(() => {
    if (!isApplicationCompleted && isPaymentInComplete) {
      return 'TELEPORT MATCHMAKING MEMBERSHIP'
    }

    if (isApplicationCompleted && isPaymentInComplete) {
      return 'You’re in the database'
    }

    if (isPaymentInComplete) {
      return 'TELEPORT MATCHMAKING MEMBERSHIP'
    }

    return 'You’re on the waitlist!'
  }, [isApplicationCompleted, isPaymentInComplete])

  const handleCompleteApplication = useCallback(() => {
    if (isProfileInComplete) {
      setShowProfileModal(true)
      return
    }

    setShowApplicationData(true)
  }, [isProfileInComplete])

  const handleApplicationDataComplete = useCallback(() => {
    setShowApplicationData(Boolean(incompleteApplicationData))
  }, [incompleteApplicationData])

  const paymentCompletedTimestamp = timestamps[TIMESTAMP_KEYS.PAYMENT_COMPLETED]

  useEffect(() => {
    refetchApplicationStatus()
  }, [paymentCompletedTimestamp, refetchApplicationStatus])

  return activityData ? (
    <>
      <div className="relative flex flex-col items-center justify-center text-white max-w-lg w-full mx-auto px-4 pt-6 md:pt-0 overflow-auto">
        {!showApplicationData ? (
          <>
            <img src={logo} alt="logo" className="mx-auto w-20 mb-4" />
            <div className="mb-12 md:mb-24">
              <div className="text-3xl font-semibold text-center uppercase">{title}</div>
              {!isPaymentInComplete && waitlistData?.position && (
                <div className="mt-6">
                  <Ranking
                    name={user.name}
                    image={user.profileImage}
                    points={user.points}
                    position={waitlistData.position}
                    me={true}
                  />
                </div>
              )}
            </div>
            {isPaymentInComplete && (
              <div className="text-center mb-4 opacity-70">
                Join Matchmaking to meet your most compatible connections today.
              </div>
            )}
            {isPaymentInComplete && (
              <ButtonLg
                className={classnames('bg-white', {
                  'rounded-full': isApplicationCompleted,
                })}
                textClassName="text-black"
                onClick={() => setShowMembershipModal(true)}
              >
                BECOME A MEMBER
              </ButtonLg>
            )}
            {(!isApplicationCompleted || isPaymentInComplete) && (
              <>
                {!isApplicationCompleted ? (
                  <ButtonLg
                    className="bg-tunaGray"
                    textClassName="text-white"
                    onClick={handleCompleteApplication}
                  >
                    JOIN DATABASE
                  </ButtonLg>
                ) : (
                  <Button
                    className="bg-tunaGray rounded-full px-8 mb-2 font-ownersWide shadow-md drop-shadow"
                    onClick={() => window.open(config.ios.appLink, '_blank')}
                  >
                    <span className="font-normal text-base">Download The App</span>
                  </Button>
                )}
              </>
            )}
          </>
        ) : (
          <UserData
            incompleteApplicationData={incompleteApplicationData}
            onComplete={handleApplicationDataComplete}
          />
        )}
        {(showProfileModal || showMembershipModal) && <ModalBackground />}
        <AuthModal
          authStep={AUTH_STEPS.PROFILE}
          show={showProfileModal}
          onClose={() => setShowProfileModal(false)}
          onComplete={() => {
            refetchApplicationStatus()
            setShowProfileModal(false)
            updateTimestamp(TIMESTAMP_KEYS.PROFILE_COMPLETED)
            setShowApplicationData(Boolean(incompleteApplicationData))
          }}
        />
        <MembershipModal
          show={showMembershipModal}
          onClose={() => setShowMembershipModal(false)}
          onComplete={() => {
            refetchApplicationStatus()
            setShowMembershipModal(false)

            setTimeout(() => {
              refetchApplicationStatus()
            }, 1000)
            setTimeout(() => {
              refetchApplicationStatus()
            }, 2500)
            setTimeout(() => {
              refetchApplicationStatus()
            }, 4000)
          }}
        />
      </div>
    </>
  ) : (
    <div />
  )
}

export default Waitlist
